import {
  EKeyboardKey,
} from '../constants/keyboard.ts';

/**
 * Returns a human-readable, platform-appropriate symbol for the given keyboard
 * key.
 *
 * - On **macOS** well-known keys are replaced with their standard glyph
 *   (e.g. `Alt` → `⌥`, `Shift` → `⇧`).
 * - On **Windows / Linux** common keys are replaced with their conventional
 *   abbreviation (e.g. `Control` → `Ctrl`, `Escape` → `Esc`).
 * - Keys without a platform-specific override (letters, digits, function keys)
 *   are returned using their `EKeyboardKey` value unchanged.
 *
 * @param key - An `EKeyboardKey` member.
 */
export function getKeyboardKeySymbol(key: EKeyboardKey): string {
  const labels = isMacOs() ? MAC_OS_KEY_LABELS : DEFAULT_KEY_LABELS;
  // Lowercase letter keys display as their uppercase equivalent so that
  // shortcut labels read naturally (e.g. Ctrl+c → Ctrl+C).
  return labels[key] ?? key.toUpperCase();
}

export function getPlatformCommandKey(): EKeyboardKey {
  return isMacOs() ? EKeyboardKey.Meta : EKeyboardKey.Control;
}

/**
 * Returns `true` when the current platform is macOS.
 *
 * Prefers the modern `navigator.userAgentData.platform` API and falls back to
 * the deprecated `navigator.platform` for environments that do not yet support
 * the newer API.
 */
function isMacOs(): boolean {
  // see https://developer.mozilla.org/en-US/docs/Web/API/Navigator/platform#determining_the_modifier_key_for_the_users_platform
  return navigator.platform.startsWith('Mac') || navigator.platform === 'iPhone';
}

/**
 * Windows / Linux use short English abbreviations for special keys.
 * Arrow keys use the same Unicode glyphs on all platforms.
 */
const DEFAULT_KEY_LABELS: Partial<Record<EKeyboardKey, string>> = {
  [EKeyboardKey.Control]: 'Ctrl',
  [EKeyboardKey.Delete]: 'Del',
  [EKeyboardKey.Escape]: 'Esc',
  [EKeyboardKey.Insert]: 'Ins',
  [EKeyboardKey.Shift]: '⇧',
  [EKeyboardKey.Enter]: '↵',
  [EKeyboardKey.Meta]: 'Win',
  [EKeyboardKey.Space]: '␣',
  [EKeyboardKey.Tab]: '⇥',
  [EKeyboardKey.ArrowUp]: '↑',
  [EKeyboardKey.ArrowDown]: '↓',
  [EKeyboardKey.ArrowLeft]: '←',
  [EKeyboardKey.ArrowRight]: '→',
};

/**
 * macOS uses symbolic glyphs for modifier and special keys rather than
 * English abbreviations.
 */
const MAC_OS_KEY_LABELS: Partial<Record<EKeyboardKey, string>> = {
  ...DEFAULT_KEY_LABELS,
  [EKeyboardKey.Alt]: '⌥',
  [EKeyboardKey.Control]: '⌃',
  [EKeyboardKey.Meta]: '⌘',
  [EKeyboardKey.Backspace]: '⌫',
  [EKeyboardKey.Delete]: '⌦',
  [EKeyboardKey.Escape]: '⎋',
};
